// Pinterest en Recrear — UNA fuente para las dos pantallas (`InterfazV2Plantillas` y
// `PlantillasBelleza`), ADR-2026-10-29-pinterest-en-recrear-con-plantillas.
//
// El chip "Pinterest" va entre "Todas" y "Subidas". Muestra lo que la persona guardó en su
// Pinterest ("Todos mis guardados") con la misma caja que una plantilla, y tocar un pin abre
// "Recrear imagen" con ese pin como la foto a replicar. Sin conectar, el chip ofrece conectarlo
// ahí mismo.
//
// NADA de Pinterest se guarda: los pines se le piden al backend cada vez que se entra al chip, se
// muestran con `<img>` directo de su link (no `CachedImg`, que los guardaría en Cache Storage) y
// el pin viaja al modo como link, por `location.state`. Mientras se prueba, el chip sólo aparece
// si el backend dice `en_recrear` (los dev testers).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoDePinterest } from '../../../chat-atlas/ui/LogoDePinterest';
import {
  abrirVentanaDePinterest,
  CANCELADO_EN_ATLAS,
  conectarPinterestEnVentana,
  desconectarPinterest,
  ErrorDePinterest,
  esAppInstaladaEnIOS,
  estadoDePinterest,
  EVENTO_CAMBIO_DE_PINTEREST,
  pinesDePinterest,
  type EstadoDePinterest,
  type PinDePinterest,
} from '../../../lib/pinterestConexion';
import { esImagenDePinterest, mensajeDeErrorDePinterest } from '../../../lib/pinterestConexionLogica';
import { ColumnasParallax } from '../modes/redes/ColumnasParallax';
import { ASPECT_CYCLE } from './TemplateMasonry';
import { RUTA_RECREAR_IMAGEN, type EstadoDeRecrearImagen, type PinParaRecrear } from './subidasParaRecrear';

/** El id del chip "Pinterest". Viaja en `?categoria=` como cualquier otra categoría. */
export const CATEGORIA_PINTEREST = 'pinterest';

/** Lo que el modo necesita de un pin: el link grande para generar y la miniatura para mostrar. */
export function pinParaRecrear(pin: PinDePinterest): PinParaRecrear {
  return { imagen: pin.imagen, miniatura: pin.miniatura, titulo: pin.titulo };
}

/** Lee el pin que llegó por `location.state`, sólo si sus dos links son de Pinterest. */
export function pinDeEntrada(state: unknown): PinParaRecrear | null {
  const pin = (state as EstadoDeRecrearImagen | null)?.pin;
  if (!pin || !esImagenDePinterest(pin.imagen) || !esImagenDePinterest(pin.miniatura)) return null;
  return { imagen: pin.imagen, miniatura: pin.miniatura, titulo: typeof pin.titulo === 'string' && pin.titulo.trim() ? pin.titulo.trim() : null };
}

const mensaje = (e: unknown) => (e instanceof ErrorDePinterest ? e.message : mensajeDeErrorDePinterest('error'));

type Pines = { items: PinDePinterest[]; siguiente: string | null; cargando: boolean; fallo: boolean };

/** Se acaba de entrar al chip: los guardados se piden de nuevo, así aparece lo que la persona
 *  guardó en Pinterest mientras tanto sin recargar la página (review #235). */
export const seEntroAlChip = (estabaActivo: boolean, activo: boolean): boolean => activo && !estabaActivo;

/**
 * Cuándo se vuelve a armar el observador del scroll infinito. Lleva el cursor además de la
 * cantidad: una página sin imágenes (Pinterest manda videos y el backend los descarta) no suma
 * pines pero trae otro cursor, y sin él el centinela, que ya estaba a la vista, no volvía a avisar
 * y la grilla quedaba cortada (review #235).
 */
export const claveDelCentinela = (pines: Pick<Pines, 'items' | 'siguiente' | 'fallo'> | null): string => {
  const hayMas = !!pines?.siguiente && !pines.fallo;
  return `${hayMas}|${pines?.items.length ?? 0}|${pines?.siguiente ?? ''}`;
};

/**
 * El estado de Pinterest y sus guardados para el chip. `activo` = el chip está elegido: recién
 * ahí se piden los pines (cada pedido gasta del cupo de Pinterest de la persona).
 */
export function usePinterestParaRecrear(activo: boolean) {
  const navigate = useNavigate();
  const [estado, setEstado] = useState<EstadoDePinterest | null>(null);
  const [pines, setPines] = useState<Pines | null>(null);
  const [conectando, setConectando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Pedidos en vuelo, por cuenta y página: el centinela puede disparar dos veces antes del próximo render. */
  const enVuelo = useRef(new Set<string>());
  /** Cada cambio de conexión tiene su número: una página de la cuenta anterior que llega tarde se ignora. */
  const version = useRef(0);
  const cancelarConexion = useRef<AbortController | null>(null);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => void (vivo.current = false);
  }, []);

  // El estado al entrar, y de nuevo si se conecta o desconecta en otra parte (el chat).
  useEffect(() => {
    const leer = () =>
      void estadoDePinterest().then((e) => {
        if (vivo.current) setEstado(e);
      });
    const alCambiar = () => {
      version.current += 1;
      setPines(null);
      leer();
    };
    leer();
    window.addEventListener(EVENTO_CAMBIO_DE_PINTEREST, alCambiar);
    return () => window.removeEventListener(EVENTO_CAMBIO_DE_PINTEREST, alCambiar);
  }, []);

  const conectada = estado?.conectada === true;

  const pedir = useCallback(async (siguiente: string | null) => {
    const deEsta = version.current;
    const clave = `${deEsta}|${siguiente ?? ''}`;
    if (enVuelo.current.has(clave)) return;
    enVuelo.current.add(clave);
    setPines((prev) => ({ items: prev?.items ?? [], siguiente: prev?.siguiente ?? null, cargando: true, fallo: false }));
    try {
      const r = await pinesDePinterest({ siguiente });
      if (!vivo.current || deEsta !== version.current) return;
      setPines((prev) => ({
        items: siguiente ? [...(prev?.items ?? []), ...r.pines] : r.pines,
        siguiente: r.siguiente,
        cargando: false,
        fallo: false,
      }));
    } catch (e) {
      if (!vivo.current || deEsta !== version.current) return;
      setPines((prev) => ({ items: prev?.items ?? [], siguiente: prev?.siguiente ?? null, cargando: false, fallo: true }));
      if (e instanceof ErrorDePinterest && e.code === 'reconectar') {
        setEstado((s) => (s ? { ...s, conectada: false, estado: 'reconectar' } : s));
        setPines(null);
        return;
      }
      setAviso(mensaje(e));
    } finally {
      enVuelo.current.delete(clave);
    }
  }, []);

  // Antes que el efecto que pide: al volver al chip, la lista vieja se descarta (y una página de
  // la entrada anterior que llegue tarde se ignora por la versión).
  const estabaActivo = useRef(false);
  useEffect(() => {
    if (seEntroAlChip(estabaActivo.current, activo)) {
      version.current += 1;
      setPines(null);
      setAviso(null);
    }
    estabaActivo.current = activo;
  }, [activo]);

  useEffect(() => {
    if (activo && conectada && pines === null) void pedir(null);
  }, [activo, conectada, pines, pedir]);

  // Abre la ventana en el MISMO click (Safari bloquea `window.open` después de un await).
  const conectar = () => {
    if (conectando) return;
    setAviso(null);
    if (esAppInstaladaEnIOS()) {
      setAviso(mensajeDeErrorDePinterest('app_instalada'));
      return;
    }
    const popup = abrirVentanaDePinterest();
    const control = new AbortController();
    cancelarConexion.current = control;
    setConectando(true);
    conectarPinterestEnVentana(popup, control.signal)
      .catch((e) => {
        if (!vivo.current) return;
        if (e instanceof ErrorDePinterest && e.code === CANCELADO_EN_ATLAS) return;
        setAviso(mensaje(e));
      })
      .finally(() => {
        if (cancelarConexion.current === control) cancelarConexion.current = null;
        if (vivo.current) setConectando(false);
      });
    // Al conectar, `conectarPinterestEnVentana` avisa el cambio y el efecto de arriba relee el estado.
  };

  const cancelar = () => cancelarConexion.current?.abort();

  const desconectar = () => {
    setAviso(null);
    desconectarPinterest().catch((e) => vivo.current && setAviso(mensaje(e)));
  };

  const reintentar = () => {
    setAviso(null);
    void pedir(pines?.items.length ? (pines.siguiente ?? null) : null);
  };

  const llegarAlFinal = () => {
    if (!pines?.siguiente || pines.cargando || pines.fallo) return;
    void pedir(pines.siguiente);
  };

  const abrirPin = useCallback(
    (pin: PinDePinterest) => {
      const state: EstadoDeRecrearImagen = { pin: pinParaRecrear(pin) };
      navigate(RUTA_RECREAR_IMAGEN, { state });
    },
    [navigate],
  );

  return {
    /** El chip se dibuja: Pinterest prendido para esta cuenta y habilitado en Recrear. */
    visible: estado?.disponible === true && estado.enRecrear,
    /** Ya se sabe si se ve o no (antes, una URL con `?categoria=pinterest` espera). */
    sabido: estado !== null,
    estado,
    pines,
    conectando,
    aviso,
    conectar,
    cancelar,
    desconectar,
    reintentar,
    llegarAlFinal,
    abrirPin,
  };
}

export type PinterestParaRecrear = ReturnType<typeof usePinterestParaRecrear>;

/** El contenido del chip "Pinterest": conectar, o los guardados en la grilla de Recrear. */
export const GrillaDePinterest: React.FC<{ pinterest: PinterestParaRecrear; quieto: boolean }> = ({ pinterest, quieto }) => {
  const { estado, pines, conectando, aviso } = pinterest;
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const centinela = useRef<HTMLDivElement | null>(null);
  const llegarAlFinal = useRef(pinterest.llegarAlFinal);
  llegarAlFinal.current = pinterest.llegarAlFinal;

  // Scroll infinito: cerca del final se pide la página siguiente (100 pines).
  const hayMas = !!pines?.siguiente && !pines.fallo;
  const clave = claveDelCentinela(pines);
  useEffect(() => {
    const el = centinela.current;
    if (!el || !hayMas || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting)) llegarAlFinal.current();
    }, { rootMargin: '800px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hayMas, clave]);

  const avisoBox = aviso ? <p className="mx-auto max-w-md pb-3 text-center text-[13px] leading-snug text-red-600">{aviso}</p> : null;

  if (!estado) return <div className="h-40" aria-busy="true" />;

  if (!estado.conectada) {
    const reconectar = estado.estado === 'reconectar';
    return (
      <div className="px-1 py-6">
        {avisoBox}
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-6 py-8 text-center">
          <LogoDePinterest className="h-12 w-12" aria-hidden="true" />
          <p className="text-[15px] font-semibold text-brand-black">
            {reconectar ? 'Volvé a conectar tu Pinterest' : 'Recreá lo que guardaste en Pinterest'}
          </p>
          <p className="text-[13px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.6)] [text-wrap:balance]">
            {reconectar
              ? 'Pinterest pidió volver a darnos permiso para ver lo que guardaste.'
              : 'Conectá tu cuenta, elegí una imagen que guardaste y Atlas la vuelve a hacer con tus productos. Nada de tu Pinterest se guarda en Atlas.'}
          </p>
          <button
            type="button"
            onClick={pinterest.conectar}
            disabled={conectando}
            className="mt-1 inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
          >
            {conectando ? 'Conectando…' : reconectar ? 'Volver a conectar' : 'Conectar Pinterest'}
          </button>
          {conectando && (
            <button type="button" onClick={pinterest.cancelar} className="text-[13px] font-semibold text-brand-primary hover:underline">
              Cancelar
            </button>
          )}
        </div>
      </div>
    );
  }

  const items = pines?.items ?? [];
  const cargandoPrimera = !pines || (pines.cargando && items.length === 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3 text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
        <span className="inline-flex items-center gap-1.5">
          <LogoDePinterest className="h-4 w-4" aria-hidden="true" />
          {estado.username ? `Todos tus guardados de @${estado.username}` : 'Todos tus guardados'}
        </span>
        {confirmarSalida ? (
          <span className="inline-flex items-center gap-2">
            <span>¿Desconectar tu Pinterest?</span>
            <button
              type="button"
              onClick={() => {
                setConfirmarSalida(false);
                pinterest.desconectar();
              }}
              className="font-semibold text-[#C0392B] hover:underline"
            >
              Desconectar
            </button>
            <button type="button" onClick={() => setConfirmarSalida(false)} className="font-semibold text-brand-primary hover:underline">
              Cancelar
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirmarSalida(true)} className="font-semibold text-brand-primary hover:underline">
            Desconectar
          </button>
        )}
      </div>
      {avisoBox}
      {cargandoPrimera ? (
        <ColumnasParallax
          quieto
          items={ASPECT_CYCLE.slice(0, 8).map((a, i) => (
            <div key={i} className={`w-full mb-3 sm:mb-4 rounded-xl bg-black/[0.06] animate-pulse ${a}`} />
          ))}
        />
      ) : items.length === 0 && !pines?.fallo ? (
        <p className="py-10 text-center text-[13.5px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
          Todavía no guardaste imágenes en tu Pinterest. Guardá alguna allá y vas a verla acá.
        </p>
      ) : (
        <ColumnasParallax
          quieto={quieto}
          items={items.map((pin, i) => (
            <CardDePin key={pin.id} pin={pin} aspectClass={ASPECT_CYCLE[i % ASPECT_CYCLE.length]} onClick={() => pinterest.abrirPin(pin)} />
          ))}
        />
      )}
      {pines?.fallo && (
        <div className="flex flex-wrap items-center justify-center gap-3 py-6 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.6)]">
          <span>No pudimos traer esto de tu Pinterest.</span>
          <button type="button" onClick={pinterest.reintentar} className="font-semibold text-brand-primary hover:underline">
            Reintentar
          </button>
        </div>
      )}
      {hayMas && <div ref={centinela} className="h-10" aria-hidden="true" />}
      {pines?.cargando && items.length > 0 && (
        <p className="py-4 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">Cargando más…</p>
      )}
    </div>
  );
};

/** Un pin, con la misma caja que una plantilla de la grilla. `<img>` directo: no se cachea. */
export const CardDePin: React.FC<{ pin: PinDePinterest; aspectClass: string; onClick: () => void }> = ({ pin, aspectClass, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={pin.titulo ?? undefined}
    className={`w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.99] transition-transform block ${aspectClass}`}
  >
    <img
      src={pin.miniatura}
      alt={pin.titulo ?? 'Imagen de Pinterest'}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
    />
  </button>
);

/** El chip "Pinterest", con el logo tal cual (las guías de marca piden su rojo sin alterar). */
export const ChipDePinterest: React.FC<{ activo: boolean; className: string; onClick: () => void }> = ({ activo, className, onClick }) => (
  <button type="button" onClick={onClick} aria-pressed={activo} className={`${className} inline-flex items-center gap-1.5`}>
    <LogoDePinterest className="h-4 w-4" aria-hidden="true" />
    Pinterest
  </button>
);
